<script setup>
import FormElSelect from "@/components/form/FormElSelect.vue";

const props = defineProps({
    // Visibility & loading
    visible: {
        type: Boolean,
        required: true,
    },
    loadingProjectDocuments: {
        type: Boolean,
        required: true,
    },
    isDeleting: {
        type: Boolean,
        required: true,
    },

    // Data
    projectsWithDocuments: {
        type: Array,
        required: true,
    },
    documentActions: {
        type: Object,
        required: true,
    },
    documentCategories: {
        type: Array,
        required: true,
    },
    allDocumentBtnBulk: {
        type: Boolean,
        required: true,
    },
    selectedDocumentBtnBulk: {
        type: Boolean,
        required: true,
    },
    bulkSelectedCategoryCount: {
        type: Number,
        required: true,
    },

    // Callbacks supplied by parent (List.vue / Search.vue)
    cancelBulkDelete: {
        type: Function,
        required: true,
    },
    toggleProjectCollapse: {
        type: Function,
        required: true,
    },
    isProjectCollapsed: {
        type: Function,
        required: true,
    },
    toggleDocumentCheck: {
        type: Function,
        required: true,
    },
    updateDocumentCategory: {
        type: Function,
        required: true,
    },
    hasAnyDocumentSelection: {
        type: Function,
        required: true,
    },
    getCheckedCount: {
        type: Function,
        required: true,
    },
    getCategoryCount: {
        type: Function,
        required: true,
    },
    performBulkDelete: {
        type: Function,
        required: true,
    },
});

// Local safe wrapper so template doesn't crash if prop is misbound
const isProjectCollapsedLocal = (projectId) => {
    if (typeof props.isProjectCollapsed === "function") {
        return props.isProjectCollapsed(projectId);
    }
    return false;
};
</script>
<template>

    <!-- Bulk Delete Modal -->
    <el-dialog :model-value="props.visible" @close="props.cancelBulkDelete" width="800px" :close-on-click-modal="false" :show-close="false"
        class="bulk-delete-modal" destroy-on-close style="border-radius: 25px; padding: 25px">
        <template #header="{ titleId, titleClass, close }" class="p-0">
            <div class="d-flex justify-content-between">
                <h4 :id="titleId" :class="titleClass" style="color: #f04438 !important">
                    Delete Projects
                </h4>
                <button type="button" class="btn p-0" @click="close">
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <path d="M15.5 5.5L5.5 15.5M5.5 5.5L15.5 15.5" stroke="#96959D" stroke-width="1.66667"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>
        </template>
        <!-- <template #header="{ titleId, titleClass }">
        <div class="my-header d-flex justify-content-between align-items-center">
          <h4 :id="titleId" :class="titleClass" style="color: #d31e58 !important">
            Delete Projects
          </h4>
          <el-icon @click="cancelBulkDelete" size="30" class="el-icon--left">
            <CircleClose />
          </el-icon>
        </div>
      </template> -->

        <div v-if="props.loadingProjectDocuments" class="text-center py-5">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading project documents...</p>
        </div>

        <div v-else>
            <div class="alert-delete alert alert-warning mb-4 rounded-16">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>
                    {{ props.projectsWithDocuments.length }} Project{{
                        props.projectsWithDocuments.length > 1 ? "s" : ""
                    }}
                    selected.</strong>
            </div>

            <div class="projects-accordion">
                <div v-for="project in props.projectsWithDocuments" :key="project.id" class="project-item mb-4">
                    <!-- Project Header with Collapse -->
                    <div class="project-header d-flex align-items-center p-3 border" :class="isProjectCollapsedLocal(project.id)
                            ? 'rounded-bottom'
                            : ''
                        " @click="props.toggleProjectCollapse(project.id)"
                        style="cursor: pointer; background-color: #f8f9fa">
                        <div class="me-2">
                            <i
                                class="bi"
                                :class="
                                    isProjectCollapsedLocal(project.id)
                                        ? 'bi-chevron-right'
                                        : 'bi-chevron-down'
                                "
                            ></i>
                        </div>
                        <div class="flex-grow-1">
                            <h5 class="mb-0 title header-title">
                                {{ project.title }}
                            </h5>
                            <div class="small text-muted" style="margin-top: 10px">
                                <span style="
                                            background: #eee;
                                            border-radius: 50%;
                                        "><img style="width: 30px; height: 35px" v-if="project.client.avatar"
                                        :src="project.client.avatar?.url" /></span>
                                <span v-if="project.clientName" class="ms-2 up-title">
                                    {{ project.clientName }}</span>
                                <span v-if="project.documents?.length" class="ms-2 up-title">
                                    {{ "| " + project.documents.length }}
                                    document{{
                                        project.documents.length > 1
                                            ? "s"
                                            : ""
                                    }}
                                </span>
                                <span v-else class="ms-2 up-title">{{
                                    " | No documents"
                                }}</span>
                            </div>
                        </div>
                        <svg v-if="!props.isProjectCollapsed(project.id)" xmlns="http://www.w3.org/2000/svg" width="12"
                            height="8" viewBox="0 0 12 8" fill="none">
                            <path
                                d="M1.5031 6.8706C1.60467 6.73612 1.90793 6.33466 2.08853 6.10324C2.45026 5.63973 2.94452 5.02382 3.47769 4.40974C4.01356 3.79256 4.57648 3.19148 5.07658 2.74961C5.32734 2.52805 5.54762 2.35969 5.72939 2.25011C5.90035 2.14704 6.00134 2.12561 6.00134 2.12561C6.00134 2.12561 6.09936 2.14705 6.27031 2.2501C6.45209 2.35968 6.67237 2.52804 6.92313 2.74961C7.42322 3.19148 7.98615 3.79256 8.52201 4.40975C9.05518 5.02384 9.54944 5.63975 9.91117 6.10327C10.0918 6.33469 10.3946 6.73558 10.4962 6.87006C10.7009 7.148 11.0925 7.20794 11.3705 7.00325C11.6484 6.79855 11.7078 6.40731 11.5031 6.12937L11.5015 6.12727C11.395 5.98624 11.0809 5.57037 10.8966 5.33423C10.5267 4.86025 10.018 4.22616 9.46589 3.59024C8.91646 2.95743 8.31173 2.30851 7.7508 1.81288C7.47105 1.5657 7.18747 1.34344 6.91567 1.17958C6.66102 1.02607 6.33861 0.874997 5.99985 0.875C5.66109 0.875003 5.33868 1.02607 5.08404 1.17959C4.81223 1.34344 4.52866 1.5657 4.24891 1.81288C3.68798 2.30851 3.08325 2.95742 2.53382 3.59023C1.98169 4.22614 1.473 4.86022 1.1031 5.3342C0.918709 5.57047 0.60464 5.98629 0.4983 6.12708L0.4969 6.12893C0.292204 6.40687 0.351284 6.79851 0.629218 7.00321C0.907143 7.2079 1.2984 7.14851 1.5031 6.8706Z"
                                fill="#211F29" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"
                            fill="none">
                            <path
                                d="M1.5031 1.1294C1.60467 1.26388 1.90793 1.66534 2.08853 1.89676C2.45026 2.36027 2.94452 2.97618 3.47769 3.59026C4.01356 4.20744 4.57648 4.80852 5.07658 5.25039C5.32734 5.47195 5.54762 5.64031 5.72939 5.74989C5.90035 5.85296 6.00134 5.87439 6.00134 5.87439C6.00134 5.87439 6.09936 5.85295 6.27031 5.7499C6.45209 5.64032 6.67237 5.47196 6.92313 5.25039C7.42322 4.80852 7.98615 4.20744 8.52201 3.59025C9.05518 2.97616 9.54944 2.36025 9.91117 1.89673C10.0918 1.66531 10.3946 1.26442 10.4962 1.12994C10.7009 0.852004 11.0925 0.792058 11.3705 0.996751C11.6484 1.20145 11.7078 1.59269 11.5031 1.87063L11.5015 1.87273C11.395 2.01376 11.0809 2.42963 10.8966 2.66577C10.5267 3.13975 10.018 3.77384 9.46589 4.40976C8.91646 5.04257 8.31173 5.69149 7.7508 6.18712C7.47105 6.4343 7.18747 6.65656 6.91567 6.82042C6.66102 6.97393 6.33861 7.125 5.99985 7.125C5.66109 7.125 5.33868 6.97393 5.08404 6.82041C4.81223 6.65656 4.52866 6.4343 4.24891 6.18712C3.68798 5.69149 3.08325 5.04258 2.53382 4.40977C1.98169 3.77386 1.473 3.13978 1.1031 2.6658C0.918709 2.42953 0.60464 2.01371 0.4983 1.87292L0.4969 1.87107C0.292204 1.59313 0.351284 1.20149 0.629218 0.996793C0.907143 0.792105 1.2984 0.851493 1.5031 1.1294Z"
                                fill="#211F29" />
                        </svg>
                        <!-- <div class="document-counts" v-if="project.documents?.length > 0">
                <span class="badge tdDanger me-1">{{
                  getCheckedCount(project.id)
                  }}</span>
                <span class="badge tdSuccess me-1">{{
                  getCategoryCount(project.id)
                  }}</span>
              </div> -->
                    </div>

                    <!-- Project Content (Collapsible) -->
                    <div v-show="!isProjectCollapsedLocal(project.id)"
                        class="project-content p-3 border border-top-0 rounded-bottom">
                        <h6 v-if="project.documents?.length > 0" class="mt-2">
                            Choose the documents that you want to delete
                            with the project and change the type of the
                            document that you want to keep it
                        </h6>
                        <div v-else class="text-muted mb-3">
                            No documents in this project
                        </div>

                        <!-- Documents list -->
                        <div v-for="doc in project.documents" :key="doc.id" class="d-flex mt-3">
                            <div class="col-md-9">
                                <div class="d-flex align-items-center gap-2">
                                    <input class="form-check-input" type="checkbox" v-model="props.documentActions[project.id][doc.id].checked"
                                        @change="props.toggleDocumentCheck(project.id, doc.id)" />
                                    <span class="px-2">
                                        <h6 class="me-2 header-sub-title">
                                            {{ doc.title }}
                                        </h6>

                                        <span class="up-title">{{
                                            doc.files_count + " Files"
                                        }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="col-md-3 text-right">
                                <!-- FormElSelect component -->
                                <FormElSelect
                                    :name="`category_${doc.id}`"
                                    :model="props.documentActions[project.id][doc.id]"
                                    :options="props.documentCategories"
                                    :disabled="props.documentActions[project.id][doc.id].checked"
                                    :placeholder="'Select another type'"
                                    @change="props.updateDocumentCategory(project.id, doc.id)"
                                />
                            </div>
                        </div>

                        <!-- Project actions -->
                        <div class="row" v-if="false && project.documents?.length > 0">
                            <div class="col-md-5"></div>
                            <div class="col-md-3">
                                <el-button class="fw-bold p-4" :disabled="props.hasAnyDocumentSelection(project.id)">
                                    Delete All Documents
                                </el-button>
                            </div>
                            <div class="col-md-3 d-flex justify-content-end">
                                <el-button :disabled="!props.hasAnyDocumentSelection(project.id)"
                                    class="fw-bold p-4 pr-3" style="background: #d31e58" type="danger">
                                    Delete {{
                                        props.getCheckedCount(project.id)
                                    }} Save {{
                                        props.getCategoryCount(project.id)
                                    }}
                                </el-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <button class="btn btn-secondary" @click="props.cancelBulkDelete" :disabled="props.isDeleting">
                    Cancel
                </button>

                <div class="row">
                    <div class="col-md-6">
                        <button
                            class="btn btn-light fw-bold p-2"
                            :disabled="
                                props.allDocumentBtnBulk ||
                                props.isDeleting ||
                                props.loadingProjectDocuments
                            "
                            @click="props.performBulkDelete"
                            style="border-radius: 8px"
                        >
                            <span
                                v-if="props.isDeleting && !props.allDocumentBtnBulk"
                                class="spinner-border spinner-border-sm me-2"
                                role="status"
                            ></span>
                            Delete project except selected documents
                            ({{ props.bulkSelectedCategoryCount }})
                        </button>
                    </div>
                    <div class="col-md-6">
                        <button class="btn btn-danger fw-bold p-2" :disabled="props.selectedDocumentBtnBulk ||
                            props.isDeleting ||
                            props.loadingProjectDocuments
                            " @click="props.performBulkDelete" style="background: #f04438; border-radius: 8px">
                            <span v-if="
                                props.isDeleting && !props.selectedDocumentBtnBulk
                            " class="spinner-border spinner-border-sm me-2" role="status"></span>
                            Delete project with all Documents
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </el-dialog>

</template>